import { useEffect, useState, type FormEvent } from 'react'
import {
  api, ApiFailure, ageLabel, genderShort,
  type Doctor, type PatientMatch, type Role, type User,
} from '../api'
import { useAuth } from '../auth'

export default function Users() {
  const { user: me } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [showNew, setShowNew] = useState(false)
  const [form, setForm] = useState({
    username: '', password: '', fullName: '', role: 'receptionist' as Role,
    doctorId: '', patientId: '',
  })
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  /* Patient logins are linked to a patient record, and the clinic has
     thousands of those - a dropdown is the wrong control, so the field is the
     same search the rest of the app uses. */
  const [patientQuery, setPatientQuery] = useState('')
  const [patientMatches, setPatientMatches] = useState<PatientMatch[]>([])
  const [pickedPatient, setPickedPatient] = useState<PatientMatch | null>(null)

  /** The account whose password is being reset, and the value being set. */
  const [resetting, setResetting] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')

  async function load() {
    const [u, d] = await Promise.all([
      api.get<{ users: User[] }>('/api/users'),
      api.get<{ doctors: Doctor[] }>('/api/doctors?includeInactive=true'),
    ])
    setUsers(u.users)
    setDoctors(d.doctors)
  }

  useEffect(() => { void load() }, [])

  useEffect(() => {
    const term = patientQuery.trim()
    if (term.length < 2) { setPatientMatches([]); return }
    let live = true
    const t = setTimeout(() => {
      void api.get<{ patients: PatientMatch[] }>(`/api/patients/search?q=${encodeURIComponent(term)}`)
        .then((r) => { if (live) setPatientMatches(r.patients) })
        .catch(() => { if (live) setPatientMatches([]) })
    }, 200)
    return () => { live = false; clearTimeout(t) }
  }, [patientQuery])

  function resetForm() {
    setForm({ username: '', password: '', fullName: '', role: 'receptionist', doctorId: '', patientId: '' })
    setPickedPatient(null)
    setPatientQuery('')
    setPatientMatches([])
  }

  async function create(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setMsg(null)
    try {
      await api.post('/api/users', {
        username: form.username,
        password: form.password,
        fullName: form.fullName,
        role: form.role,
        doctorId: form.role === 'doctor' ? form.doctorId || null : null,
        patientId: form.role === 'patient' ? pickedPatient?.id ?? null : null,
      })
      resetForm()
      setShowNew(false)
      await load()
      setMsg({ kind: 'ok', text: 'Login created. The user must change the password at first sign-in.' })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not create the login.' })
    } finally {
      setBusy(false)
    }
  }

  /**
   * Resetting is how a locked-out user gets back in, and the endpoint has
   * always existed - it just had no control on this screen, only on the phone
   * one. The new password is temporary: the server flags the account and
   * drops every session that user has open.
   */
  async function submitReset(e: FormEvent) {
    e.preventDefault()
    if (!resetting) return
    setBusy(true)
    setMsg(null)
    try {
      await api.post(`/api/users/${resetting.id}/reset-password`, { newPassword })
      const who = resetting.fullName
      setResetting(null)
      setNewPassword('')
      await load()
      setMsg({
        kind: 'ok',
        text: `Password reset for ${who}. They are signed out everywhere and must set their own at next sign-in.`,
      })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not reset the password.' })
    } finally {
      setBusy(false)
    }
  }

  async function toggleActive(u: User) {
    setMsg(null)
    try {
      await api.patch(`/api/users/${u.id}`, { isActive: !u.isActive })
      await load()
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not update.' })
    }
  }

  return (
    <>
      <div className="page-head">
        <h2>Staff logins</h2>
        <p>
          Deactivating a login signs that person out everywhere immediately. Accounts are never
          deleted, so the audit trail stays intact.
        </p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      <div className="panel">
        <h3>Accounts</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Username</th><th>Role</th><th>Last sign-in</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.fullName}
                    {u.mustChangePassword && <> <span className="pill warn">temp password</span></>}
                  </td>
                  <td><code>{u.username}</code></td>
                  <td><span className="pill on">{u.role}</span></td>
                  <td className="num">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td><span className={`pill ${u.isActive ? 'on' : 'off'}`}>{u.isActive ? 'active' : 'inactive'}</span></td>
                  <td>
                    <button className="link" onClick={() => { setResetting(u); setNewPassword('') }}>
                      Reset password
                    </button>
                    {u.id !== me?.id && (
                      <>
                        {' · '}
                        <button className="link" onClick={() => void toggleActive(u)}>
                          {u.isActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="actions">
          <button className="secondary" onClick={() => setShowNew((v) => !v)}>
            {showNew ? 'Cancel' : 'Add a login'}
          </button>
        </div>
      </div>

      {showNew && (
        <form className="panel" onSubmit={(e) => void create(e)}>
          <h3>New login</h3>
          <div className="grid two">
            <div className="field">
              <label htmlFor="fn">Full name</label>
              <input id="fn" value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
            </div>
            <div className="field">
              <label htmlFor="un">Username</label>
              <input id="un" value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })} required />
            </div>
            <div className="field">
              <label htmlFor="pw">Temporary password</label>
              <input id="pw" type="text" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              <span className="hint">At least 8 characters. The user changes it at first sign-in.</span>
            </div>
            <div className="field">
              <label htmlFor="role">Role</label>
              <select id="role" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
                <option value="receptionist">Receptionist</option>
                <option value="doctor">Doctor</option>
                <option value="admin">Admin</option>
                <option value="patient">Patient</option>
              </select>
            </div>
            {form.role === 'doctor' && (
              <div className="field">
                <label htmlFor="doc">Linked doctor record</label>
                <select id="doc" value={form.doctorId}
                  onChange={(e) => setForm({ ...form, doctorId: e.target.value })} required>
                  <option value="">Select a doctor…</option>
                  {doctors.map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
                </select>
              </div>
            )}
            {form.role === 'patient' && (
              <div className="field">
                <label htmlFor="pat">Linked patient record</label>
                {pickedPatient ? (
                  <div className="picked-patient">
                    <span>
                      <strong>{pickedPatient.fullName}</strong> · {pickedPatient.patientCode}
                      {pickedPatient.mobile && <>{' · '}{pickedPatient.mobile}</>}
                    </span>
                    <button type="button" className="link" onClick={() => setPickedPatient(null)}>Change</button>
                  </div>
                ) : (
                  <>
                    <input id="pat" value={patientQuery} placeholder="Search name, mobile or code"
                      onChange={(e) => setPatientQuery(e.target.value)} />
                    <div className="modal-matches">
                      {patientMatches.map((p) => (
                        <button key={p.id} type="button" className="ac-item"
                          onClick={() => { setPickedPatient(p); setPatientQuery('') }}>
                          <div>{p.fullName} <span className="generic">{ageLabel(p)}/{genderShort(p.gender)}</span></div>
                          <div className="generic">{[p.patientCode, p.mobile].filter(Boolean).join(' · ')}</div>
                        </button>
                      ))}
                      {patientQuery.trim().length >= 2 && patientMatches.length === 0 && (
                        <div className="ac-empty">No match. Register the patient at reception first.</div>
                      )}
                    </div>
                    <span className="hint">
                      A patient login only ever sees its own record, on the mobile app.
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="actions">
            <button
              type="submit"
              disabled={busy || (form.role === 'patient' && !pickedPatient)}
            >
              Create login
            </button>
          </div>
        </form>
      )}

      {resetting && (
        <div className="modal-backdrop" onClick={() => setResetting(null)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => void submitReset(e)}>
            <h3>Reset password for {resetting.fullName}</h3>
            <p className="panel-note">
              Sets a temporary password and signs {resetting.fullName.split(' ')[0]} out of every
              device. They choose their own at the next sign-in.
            </p>
            <div className="field">
              <label htmlFor="np">Temporary password</label>
              <input id="np" type="text" autoFocus value={newPassword} required
                onChange={(e) => setNewPassword(e.target.value)} />
              <span className="hint">At least 8 characters, not all digits.</span>
            </div>
            <div className="actions">
              <button type="submit" disabled={busy || newPassword.length < 8}>Reset password</button>
              <button type="button" className="secondary" onClick={() => setResetting(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </>
  )
}
